import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';

import * as Core from '../../core';
import { HollowButton, IconButton, SolidButton } from '../../components/buttons-visuals';
import { ContentContainer } from '../../components/containers';
import ConfirmModal from '../../components/confirmModal';
import { ProvaService } from '../../services/provaService';

const ProvaCatalog = (): JSX.Element => {
    const [games, setGames] = useState<Core.Models.ProvaCatalogGame[]>([]);
    const [titles, setTitles] = useState<Core.Models.ProvaCatalogTitle[]>([]);
    const [selectedGameId, setSelectedGameId] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSeeding, setIsSeeding] = useState(false);
    const [csvText, setCsvText] = useState('');
    const [titleToDelete, setTitleToDelete] = useState<Core.Models.ProvaCatalogTitle | undefined>();
    const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
    const [isDeletingAll, setIsDeletingAll] = useState(false);

    const loadGamesAndTitles = useCallback(async (preferredGameId?: string) => {
        setIsLoading(true);
        try {
            const gameList = await ProvaService.getCatalogGames();
            setGames(gameList);
            const gameId =
                (preferredGameId && gameList.some((g) => g.id === preferredGameId) ? preferredGameId : undefined) ||
                gameList[0]?.id;
            if (gameId) {
                setSelectedGameId(gameId);
                setTitles(await ProvaService.getCatalogTitles(gameId));
            } else {
                setSelectedGameId('');
                setTitles([]);
            }
        } catch (e) {
            toast.error(Core.API.getErrorMessage(e));
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadGamesAndTitles();
    }, [loadGamesAndTitles]);

    const selectedGame = useMemo(() => games.find((g) => g.id === selectedGameId), [games, selectedGameId]);

    const seedCatalog = async () => {
        setIsSeeding(true);
        try {
            await ProvaService.ensureCatalog();
            toast.success('Practice catalog initialized and titles seeded.');
            await loadGamesAndTitles();
        } catch (e) {
            toast.error(Core.API.getErrorMessage(e));
        } finally {
            setIsSeeding(false);
        }
    };

    const onGameChange = async (gameId: string) => {
        setSelectedGameId(gameId);
        try {
            setTitles(await ProvaService.getCatalogTitles(gameId));
        } catch (e) {
            toast.error(Core.API.getErrorMessage(e));
        }
    };

    const importCsv = async () => {
        if (!csvText.trim()) {
            toast.error('Paste CSV content first.');
            return;
        }
        try {
            const added = await ProvaService.importTitles(selectedGameId || undefined, csvText);
            toast.success(`Imported ${added} new title(s).`);
            setCsvText('');
            await loadGamesAndTitles(selectedGameId);
        } catch (e) {
            toast.error(Core.API.getErrorMessage(e));
        }
    };

    const confirmDeleteTitle = async () => {
        if (!titleToDelete) return;
        try {
            await ProvaService.deleteTitle(titleToDelete.id);
            setTitles((prev) => prev.filter((t) => t.id !== titleToDelete.id));
            setTitleToDelete(undefined);
            toast.success('Title deleted.');
        } catch (e) {
            toast.error(Core.API.getErrorMessage(e));
        }
    };

    const confirmDeleteAllTitles = async () => {
        if (!selectedGameId) return;
        setIsDeletingAll(true);
        try {
            const deleted = await ProvaService.deleteAllTitles(selectedGameId);
            setTitles([]);
            setConfirmDeleteAll(false);
            toast.success(`Deleted ${deleted} title(s).`);
        } catch (e) {
            toast.error(Core.API.getErrorMessage(e));
        } finally {
            setIsDeletingAll(false);
        }
    };

    const gameImageUrl = selectedGame?.cardUrl || selectedGame?.iconUrl;

    return (
        <ContentContainer shade={Core.Models.Shades.Dark40}>
            <h3 className="heading-3">Practice Catalog</h3>
            <p className="mb2x">
                Manage Prova Practice games and launchable title keys. Seed creates The Climb, Vibe Check, Own The Room,
                ICY ELE (I / II Paper Cut / III Linked), and Mic Drop, then imports the full titles CSV. Art comes from
                the game record.
            </p>

            <div className="disp-flex flex-gap mb2x">
                <SolidButton as="button" onClick={seedCatalog} pending={isSeeding} size="small">
                    Initialize / seed catalog
                </SolidButton>
                <HollowButton
                    as="button"
                    onClick={() => void loadGamesAndTitles(selectedGameId)}
                    size="small"
                    disabled={isLoading}
                >
                    Refresh
                </HollowButton>
            </div>

            {games.length > 0 && (
                <label className="disp-block mb2x">
                    Game
                    <select
                        className="full-width"
                        value={selectedGameId}
                        onChange={(e) => void onGameChange(e.target.value)}
                    >
                        {games.map((g) => (
                            <option key={g.id} value={g.id}>
                                {g.name}
                            </option>
                        ))}
                    </select>
                </label>
            )}

            {selectedGame && (
                <>
                    <div className="disp-flex align-center justify-between flex-gap flex-wrap mb2x">
                        <div className="disp-flex align-center flex-gap">
                            {gameImageUrl && (
                                <img
                                    src={gameImageUrl}
                                    alt={selectedGame.name}
                                    style={{ width: '4rem', height: '4rem', objectFit: 'cover' }}
                                />
                            )}
                            <p className="mb0">{titles.length} title(s)</p>
                        </div>
                        <HollowButton
                            as="button"
                            color="destructive"
                            size="small"
                            disabled={titles.length === 0 || isDeletingAll}
                            onClick={() => setConfirmDeleteAll(true)}
                        >
                            Delete all titles
                        </HollowButton>
                    </div>

                    <label className="disp-block mb">
                        Import CSV (paste)
                        <textarea
                            className="full-width"
                            rows={4}
                            value={csvText}
                            onChange={(e) => setCsvText(e.target.value)}
                            placeholder="id,name,playvs_title_key"
                        />
                    </label>
                    <SolidButton as="button" className="mb2x" onClick={() => void importCsv()} size="small">
                        Import CSV
                    </SolidButton>

                    <div style={{ maxHeight: '40rem', overflow: 'auto' }}>
                        <table className="full-width">
                            <thead>
                                <tr>
                                    <th>Id</th>
                                    <th>Name</th>
                                    <th>Title key</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {titles.map((t) => (
                                    <tr key={t.id}>
                                        <td>{t.externalId ?? '—'}</td>
                                        <td>{t.name}</td>
                                        <td>
                                            <code>{t.titleKey}</code>
                                        </td>
                                        <td>
                                            <IconButton
                                                as="button"
                                                buttonLabel="Delete title"
                                                buttonSize="small"
                                                color="destructive"
                                                onClick={() => setTitleToDelete(t)}
                                            >
                                                <FontAwesomeIcon icon={['fas', 'trash']} />
                                            </IconButton>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {!isLoading && games.length === 0 && (
                <p>
                    No Practice games yet. Click Initialize / seed catalog to create the seven games and import all
                    titles.
                </p>
            )}

            {titleToDelete && (
                <ConfirmModal
                    onCancel={() => setTitleToDelete(undefined)}
                    onConfirm={confirmDeleteTitle}
                    title="Delete title?"
                >
                    <p>
                        Delete <strong>{titleToDelete.name}</strong> from the catalog? This cannot be undone.
                    </p>
                </ConfirmModal>
            )}

            {confirmDeleteAll && selectedGame && (
                <ConfirmModal
                    onCancel={() => setConfirmDeleteAll(false)}
                    onConfirm={confirmDeleteAllTitles}
                    title="Delete all titles?"
                >
                    <p>
                        Delete all <strong>{titles.length}</strong> title(s) for{' '}
                        <strong>{selectedGame.name}</strong>? You can re-seed or import CSV afterward. This cannot be
                        undone.
                    </p>
                </ConfirmModal>
            )}
        </ContentContainer>
    );
};

export default ProvaCatalog;
